import React, { useEffect, useMemo, useState } from "react";
import { cleanCoCoName } from "../helpers/cocoNameHelper";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { FiSearch, FiX } from "react-icons/fi";
import { FaCog } from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import { useFeatureFlags } from "../context/FeatureFlagsContext";
import { useTranslation } from "../context/TranslationContext";
import { searchClientsAdvanced, searchClientsGlobal } from "../helpers/apiHelper";
import {
  GLOBAL_SEARCH_TYPE_OPTIONS,
  GLOBAL_SEARCH_PAGE_LIMIT,
  GLOBAL_SEARCH_TYPES,
  canUseAdvancedClientSearch,
  getLegacySearchPath,
  mapGlobalSearchError,
  normalizeGlobalSearchType,
  validateGlobalSearch,
} from "../helpers/globalSearchHelper";
import {
  ADVANCED_SEARCH_MODE,
  COCO_NAME_PARAM,
  MODE_PARAM,
  SEARCH_ROLES,
  buildCoCoClientRecordPath,
  emptyAdvancedCriteria,
  getActiveCriteria,
  hasAdvancedAnchor,
  getAdvancedHighlightQueries,
  hasAdvancedParams,
  parseAdvancedCriteria,
  serializeAdvancedCriteria,
  validateAdvancedCriteria,
} from "../helpers/advancedSearchHelper";
import AdvancedSearchPanel from "./AdvancedSearchPanel";
import Breadcrumb from "./Breadcrumb";
import SearchModeToggle from "./SearchModeToggle";
import GlobalSearchResultsTable from "./GlobalSearchResultsTable";
import "../styles/Views.css";
import "../styles/CoordinatorGlobalSearch.css";
import "../styles/AdvancedSearch.css";

const SKELETON_ROWS = 8;

// Fail closed: the page is only reachable with the flag on. Coordinator access
// that is still unknown (e.g. after a refresh) is enforced by the backend.
// Deep links/refresh: wait for the flags instead of redirecting on defaults.
const FlagsLoading = () => {
  const { t } = useTranslation();
  return (
    <div className="protected-route-loading" role="status" aria-label={t("loading")}>
      <FaCog className="spinner-cog" aria-hidden="true" />
    </div>
  );
};

export const CoordinatorGlobalSearchRoute = () => {
  const { features, ready = true } = useFeatureFlags();
  const { user } = useAuth();

  if (!ready) return <FlagsLoading />;

  // Simple search: coordinatorGlobalSearch; advanced mode: advancedClientSearch.
  if (
    (features?.coordinatorGlobalSearch !== true && !canUseAdvancedClientSearch(features))
    || user?.capabilities?.canAccessCoordinator === false
  ) {
    return <Navigate to={getLegacySearchPath(features)} replace />;
  }

  return <CoordinatorGlobalSearch />;
};

// CoCo advanced search (own clients only; scope enforced by the backend).
export const CoCoAdvancedSearchRoute = () => {
  const { features, ready = true } = useFeatureFlags();
  if (!ready) return <FlagsLoading />;
  if (!canUseAdvancedClientSearch(features)) {
    return <Navigate to={getLegacySearchPath(features)} replace />;
  }
  return <CoordinatorGlobalSearch role={SEARCH_ROLES.COCO} />;
};

const cocoRecordState = (result) => ({ partyId: result?.partyId, matterName: result?.matterName });

const chipValue = (t, field, value, criteria) => {
  if (field === "cocoId") return cleanCoCoName(criteria[COCO_NAME_PARAM]) || value;
  if (field === "caseTab") return t(`advancedSearchForm.caseTabChips.${value}`);
  if (field === "status") return t(`advancedSearchForm.statuses.${value}`);
  if (field === "clientType") return t(`advancedSearchForm.clientTypes.${value}`);
  return value;
};

const CoordinatorGlobalSearch = ({ role = SEARCH_ROLES.COORDINATOR }) => {
  const { t } = useTranslation();
  const { tokens, setUser } = useAuth();
  const { features } = useFeatureFlags();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const isCoCo = role === SEARCH_ROLES.COCO;
  const stateQuery = typeof location.state?.query === "string" ? location.state.query.trim() : "";
  const urlQuery = searchParams.get("q") || "";
  const urlType = normalizeGlobalSearchType(searchParams.get("type"));
  const simpleEnabled = !isCoCo && features?.coordinatorGlobalSearch === true;
  const advancedEnabled = canUseAdvancedClientSearch(features);
  // CoCos only have the advanced search here (never GLOBAL); their simple search is the legacy page.
  const advanced = isCoCo || (advancedEnabled && (!simpleEnabled || hasAdvancedParams(searchParams)));
  const showModeToggle = isCoCo || (simpleEnabled && advancedEnabled);
  const paramsKey = searchParams.toString();
  const urlCriteria = useMemo(
    () => parseAdvancedCriteria(new URLSearchParams(paramsKey), role),
    [paramsKey, role],
  );
  const activeCriteria = useMemo(() => getActiveCriteria(urlCriteria, role), [urlCriteria, role]);
  const slowSearch = advanced && !hasAdvancedAnchor(urlCriteria);
  const advancedKey = advanced ? JSON.stringify(serializeAdvancedCriteria(urlCriteria, role)) : "";

  const [query, setQuery] = useState(urlQuery);
  const [searchType, setSearchType] = useState(urlType);
  const [validationError, setValidationError] = useState(null);
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Callers may hand the query over in navigation state; keep it in the URL.
  useEffect(() => {
    if (!advanced && !searchParams.get("q") && stateQuery) {
      setSearchParams(
        { q: stateQuery, type: normalizeGlobalSearchType(searchParams.get("type")) },
        { replace: true },
      );
    }
  }, [advanced, searchParams, setSearchParams, stateQuery]);

  useEffect(() => {
    setQuery(urlQuery);
    setSearchType(urlType);
  }, [urlQuery, urlType]);

  const idToken = tokens?.idToken;

  useEffect(() => {
    setResponse(null);
    setError(null);
    let request = null;
    if (advanced) {
      setValidationError(null);
      if (!activeCriteria.length) return undefined;
      if (Object.keys(validateAdvancedCriteria(urlCriteria, role)).length) return undefined;
      request = (signal) => searchClientsAdvanced(urlCriteria, role, idToken, signal);
    } else {
      if (!urlQuery.trim()) {
        setValidationError(null);
        return undefined;
      }
      const invalid = validateGlobalSearch(urlQuery, urlType);
      setValidationError(invalid);
      if (invalid) return undefined;
      request = (signal) => searchClientsGlobal(urlQuery, urlType, idToken, signal, GLOBAL_SEARCH_PAGE_LIMIT);
    }
    if (!idToken) return undefined;

    const controller = new AbortController();
    setLoading(true);
    request(controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setResponse(data || { results: [] });
        if (isCoCo) return;
        setUser?.((currentUser) => (
          currentUser?.capabilities?.canAccessCoordinator === true
            ? currentUser
            : {
                ...(currentUser || {}),
                capabilities: {
                  ...(currentUser?.capabilities || {}),
                  canAccessCoordinator: true,
                },
              }
        ));
      })
      .catch((requestError) => {
        if (requestError?.name === "AbortError" || controller.signal.aborted) return;
        console.error("Client search failed:", requestError);
        setError(mapGlobalSearchError(requestError));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => {
      controller.abort();
      setLoading(false);
    };
    // advancedKey captures urlCriteria/activeCriteria.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advanced, advancedKey, idToken, isCoCo, role, setUser, urlQuery, urlType]);

  const handleSubmit = (event) => {
    event.preventDefault();
    const invalid = validateGlobalSearch(query, searchType);
    setValidationError(invalid);
    if (invalid) return;
    setSearchParams({ q: query.trim(), type: searchType });
  };

  const handleModeChange = (mode) => {
    if (mode === ADVANCED_SEARCH_MODE) {
      setSearchParams({ [MODE_PARAM]: ADVANCED_SEARCH_MODE });
    } else if (isCoCo) {
      navigate(getLegacySearchPath(features));
    } else {
      setSearchParams({});
    }
  };

  const handleAdvancedSearch = (criteria) => {
    setSearchParams(serializeAdvancedCriteria(criteria, role));
  };

  const handleAdvancedClear = () => {
    setSearchParams({ [MODE_PARAM]: ADVANCED_SEARCH_MODE });
  };

  const removeCriterion = (field) => {
    const next = { ...urlCriteria, [field]: emptyAdvancedCriteria()[field] };
    if (field === "cocoId") next[COCO_NAME_PARAM] = "";
    setSearchParams(serializeAdvancedCriteria(next, role));
  };

  const results = Array.isArray(response?.results) ? response.results : [];
  const totalFound = Number.isFinite(response?.totalFound)
    ? response.totalFound
    : results.length;
  // When truncated, totalFound may be a lower bound (backend source cap).
  const countLabel = t(
    totalFound === 1 && !response?.truncated
      ? "globalSearch.resultsCount.one"
      : "globalSearch.resultsCount.other",
  ).replace(
    "{count}",
    `${totalFound.toLocaleString("pt-BR")}${response?.truncated ? "+" : ""}`,
  );

  const breadcrumbItems = isCoCo
    ? [
        { label: t("breadcrumb.home"), link: "/" },
        { label: t("breadcrumb.clientSearch"), link: getLegacySearchPath(features) },
        { label: t("breadcrumb.advancedSearch") },
      ]
    : [
        {
          label: t("breadcrumb.coordinatorDashboard"),
          link: "/",
          state: { activePortal: "coordinator" },
        },
        { label: t("breadcrumb.globalSearch") },
      ];

  const tableProps = isCoCo
    ? { getRecordPath: buildCoCoClientRecordPath, getRecordState: cocoRecordState, hideCoCounsel: true }
    : {};

  const criteriaChips = advanced && activeCriteria.length > 0 ? (
    <ul className="adv-search-chips" aria-label={t("advancedSearchForm.chipsLabel")}>
      {activeCriteria.map(({ field, value }) => {
        const label = t(`advancedSearchForm.fields.${field}`);
        return (
          <li key={field} className="adv-search-chip">
            <span className="adv-search-chip-label">{label}:</span>
            <span className="adv-search-chip-value">{chipValue(t, field, value, urlCriteria)}</span>
            <button
              type="button"
              className="adv-search-chip-remove"
              aria-label={t("advancedSearchForm.removeChip").replace("{label}", label)}
              title={t("advancedSearchForm.removeChip").replace("{label}", label)}
              onClick={() => removeCriterion(field)}
            >
              <FiX aria-hidden="true" />
            </button>
          </li>
        );
      })}
    </ul>
  ) : null;

  const resultsSummary = response ? (
    <div className="global-search-results-header">
      <strong>{countLabel}</strong>
      {response.truncated && (
        <span className="global-search-truncated">
          {t("globalSearch.truncated").replace("{count}", results.length)}
        </span>
      )}
    </div>
  ) : null;

  return (
    <div className={`portal-page global-search-page${advanced ? " is-advanced" : ""}`}>
      <div className="portal-page-breadcrumb">
        <Breadcrumb items={breadcrumbItems} />
      </div>
      <header className="portal-page-header global-search-page-header">
        <div>
          <p className="portal-page-kicker">
            {t(isCoCo ? "breadcrumb.clientSearch" : "portal.coordinator")}
          </p>
          <h2 className="portal-page-title">
            {t(isCoCo ? "advancedSearchForm.cocoTitle" : "globalSearch.title")}
          </h2>
          <p className="portal-page-subtitle">
            {t(isCoCo ? "advancedSearchForm.cocoSubtitle" : "globalSearch.subtitle")}
          </p>
        </div>
        {showModeToggle && (
          <SearchModeToggle mode={advanced ? ADVANCED_SEARCH_MODE : "simple"} onChange={handleModeChange} />
        )}
      </header>

      {advanced ? (
        <AdvancedSearchPanel
          role={role}
          criteria={urlCriteria}
          onSearch={handleAdvancedSearch}
          onClear={handleAdvancedClear}
          loading={loading}
          idToken={idToken}
          footer={criteriaChips}
        />
      ) : (
        <section className="portal-page-panel">
          <form className="global-search-form" onSubmit={handleSubmit} noValidate>
            <div className="global-search-field global-search-type">
              <label className="portal-label" htmlFor="globalSearchType">
                {t("globalSearch.typeLabel")}
              </label>
              <select
                id="globalSearchType"
                className="portal-input global-search-select"
                value={searchType}
                onChange={(event) => {
                  setSearchType(event.target.value);
                  setValidationError(null);
                }}
              >
                {GLOBAL_SEARCH_TYPE_OPTIONS.map((type) => (
                  <option key={type} value={type}>
                    {t(`globalSearch.types.${type}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="global-search-field global-search-query">
              <label className="portal-label" htmlFor="globalSearchQuery">
                {t("globalSearch.queryLabel")}
              </label>
              <input
                id="globalSearchQuery"
                type="search"
                className={`portal-input global-search-input ${validationError ? "invalid" : ""}`}
                autoComplete="off"
                value={query}
                placeholder={t(`globalSearch.placeholders.${searchType}`)}
                inputMode={
                  {
                    [GLOBAL_SEARCH_TYPES.EMAIL]: "email",
                    [GLOBAL_SEARCH_TYPES.CPF]: "numeric",
                    [GLOBAL_SEARCH_TYPES.CNPJ]: "numeric",
                    [GLOBAL_SEARCH_TYPES.POSTCODE]: "numeric",
                  }[searchType] || "text"
                }
                aria-invalid={Boolean(validationError)}
                aria-describedby={validationError ? "global-search-validation" : undefined}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setValidationError(null);
                }}
              />
            </div>
            <button type="submit" className="portal-button global-search-submit" disabled={loading}>
              <FiSearch aria-hidden="true" />
              {loading ? t("globalSearch.searching") : t("globalSearch.button")}
            </button>
          </form>
          {validationError && (
            <p id="global-search-validation" className="portal-feedback error global-search-validation">
              {t(`globalSearch.validation.${validationError}`)}
            </p>
          )}
        </section>
      )}

      <section
        className="portal-page-section global-search-results"
        aria-live="polite"
        aria-busy={loading}
      >
        {loading && (
          <div
            className="client-table-shell global-search-skeleton"
            role="status"
            aria-label={t("globalSearch.searching")}
          >
            <div className="global-search-loading-banner">
              <span className="global-search-loading-spinner" aria-hidden="true" />
              <div className="global-search-loading-text">
                <strong>{t("globalSearch.searching")}</strong>
                {slowSearch && (
                  <span className="global-search-loading-hint">{t("advancedSearchForm.slowSearchHint")}</span>
                )}
              </div>
            </div>
            {Array.from({ length: SKELETON_ROWS }, (_, index) => (
              <div key={index} className="global-search-skeleton-row" aria-hidden="true" />
            ))}
          </div>
        )}

        {!loading && error && (
          <p className="portal-feedback error" role="alert">
            {t(`globalSearch.errors.${error}`)}
          </p>
        )}

        {!loading && !error && response && (
          <>
            {results.length === 0 && response.truncated ? (
              // Backend deadline hit before any match was collected: not a real "no results".
              <p className="global-search-empty is-timeout" role="status">
                {t("globalSearch.timedOut")}
              </p>
            ) : results.length === 0 ? (
              <>
                {resultsSummary}
                <p className="global-search-empty">{t("globalSearch.empty")}</p>
              </>
            ) : (
              <GlobalSearchResultsTable
                summary={resultsSummary}
                results={results}
                query={advanced ? "" : response.query || urlQuery}
                highlightQueries={advanced ? getAdvancedHighlightQueries(urlCriteria) : null}
                {...tableProps}
              />
            )}
          </>
        )}

        {!loading && !error && !response && (
          <p className="global-search-empty">
            {t(advanced ? "advancedSearchForm.prompt" : "globalSearch.prompt")}
          </p>
        )}
      </section>
    </div>
  );
};

export default CoordinatorGlobalSearch;
